import React, { TextareaHTMLAttributes, useEffect, useRef } from 'react'
import {
  FieldValues,
  UseFormRegister,
  Path,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form'
import './FormInput.css'
import { isEmpty } from 'lodash'
import { NAMESPACE } from '../../utils'

interface MultiLineInputPropTypes<T extends FieldValues>
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: Path<T>
  label?: string
  register?: UseFormRegister<T>
  errors?: FieldErrors<T>
  className?: string
  validation?: RegisterOptions<T, Path<T>>
  widthClass?: string
  rows?: number
  dataQaId?: string
  hasPermission?: boolean
  autoSize?: boolean
  customStyles?: object
  dynamicHeight?: boolean
  renderHTML?: boolean
  isHelperTextVisible?: string
}

const MultiLineInput = <T extends FieldValues>({
  name,
  label,
  disabled = false,
  placeholder = '',
  defaultValue,
  register,
  errors,
  validation,
  className = '',
  widthClass = 'max-h-[70px]',
  rows = 2,
  dataQaId = '',
  hasPermission = true,
  autoSize = false,
  customStyles = {},
  dynamicHeight = false,
  renderHTML = false,
  isHelperTextVisible = '',
  ...rest
}: MultiLineInputPropTypes<T>) => {
  const divRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (autoSize) {
      if (divRef.current) {
        const textarea = divRef.current.querySelector('textarea')
        if (textarea) {
          textarea.style.height = 'auto'
          textarea.style.height = textarea.scrollHeight + 'px'
        }
      }
    }
  }, [])
  useEffect(() => {
    if (!dynamicHeight || !divRef.current) return

    const textarea = divRef.current.querySelector('textarea')
    if (!textarea) return

    const adjustHeight = () => {
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }

    textarea.style.overflow = 'hidden'
    adjustHeight()
    textarea.addEventListener('input', adjustHeight)

    return () => {
      textarea.removeEventListener('input', adjustHeight)
    }
  }, [dynamicHeight])
  return (
    <div className="w-full font-primary" data-qa-id={dataQaId || ''}>
      <div className="custom-input-main-container group font-extraLight w-full inline-flex relative min-w-0 p-0 m-0 border-0 align-top font-primary">
        <div
          className={`custom-input-label flex text-base p-0 origin-top-left truncate max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-[1] pointer-events-none ${
            disabled ? 'text-accent-300' : 'text-black'
          }`}
        >
          {label ? (
            <label
              className={`font-extraLight block text-xs leading-1 truncate max-w-[calc(100% - 1.5rem)]
              group-focus-within:max-w-[calc(100% - 1.5rem)]  ${
                disabled
                  ? 'text-accent-300'
                  : 'group-focus-within:text-secondary-500 group-hover:text-secondary-500'
              }`}
              title={label}
              style={{ ...customStyles }}
            >
              {label}
            </label>
          ) : null}

          {validation?.required || rest?.required ? (
            <span
              className={`ml-1 text-xs leading-1 text-error-500 ${
                disabled
                  ? 'text-accent-300'
                  : 'text-current active-input-required'
              }`}
              style={{ ...customStyles }}
            >
              *
            </span>
          ) : null}
        </div>
        <div
          className="custom-input-container filled-inp w-full box-border relative cursor-text inline-flex items-center rounded"
          ref={divRef}
        >
          {renderHTML ? (
            <div
              className={`px-4 py-2 text-sm font-light rounded w-full min-h-9 ${
                disabled
                  ? 'bg-accent-50 text-accent-500 cursor-not-allowed'
                  : 'bg-transparent text-current'
              } ${className}`}
              style={{ whiteSpace: 'pre-wrap', ...customStyles }}
              dangerouslySetInnerHTML={{ __html: defaultValue || '' }}
            ></div>
          ) : (
            <textarea
              placeholder={placeholder}
              className={`px-4 leading-[1.69] h-10 my-2 custom-input-main ${widthClass} resize-none font-light outline-none text-current border-none box-content bg-transparent m-0 block min-w-0 w-full px-4 py-2 rounded text-base disabled:bg-accent-50 disabled:text-accent-500 ${className} ${
                isEmpty(placeholder) ? '' : 'custom-input-main-placeholder'
              }`}
              style={{ ...customStyles }}
              disabled={disabled}
              defaultValue={defaultValue}
              {...(hasPermission && register
                ? register(name, { ...validation })
                : { name })}
              {...(!hasPermission ? { value: NAMESPACE.REDACTED } : {})}
              {...rest}
              rows={autoSize ? undefined : rows}
            />
          )}
          <fieldset
            aria-hidden="true"
            className={`text-left absolute bottom-0 ${
              label ? 'top-[-0.313rem]' : 'top-0'
            } left-0 right-0 m-0 px-2 pointer-events-none rounded border-solid border overflow-hidden min-w-0 border-border-150 group-focus-within:border-secondary-500 group-focus-within:border ${
              disabled
                ? 'border-border-200 '
                : 'group-hover:border-secondary-500'
            } ${!isEmpty(errors?.[name]) ? 'border-error-500' : ''}`}
          >
            {label ? (
              <legend className="custom-input-legend invisible p-0 block w-auto overflow-hidden max-w-0 whitespace-nowrap box-border h-3 group-focus-within:max-w-full group-focus-within:p-1">
                <span
                  className="l-span text-xs font-primary"
                  style={{ ...customStyles }}
                >
                  {label}
                </span>
                {validation?.required ? (
                  <span className="ml-2" style={{ ...customStyles }}>
                    *
                  </span>
                ) : null}
              </legend>
            ) : null}
          </fieldset>
        </div>
      </div>
      {isHelperTextVisible ? (
        <div
          className="ck-content break-words"
          dangerouslySetInnerHTML={{ __html: isHelperTextVisible }}
        ></div>
      ) : null}
      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === NAMESPACE.REQUIRED_LOWERCASE &&
        !errors?.[name]?.['message'] && (
          <span
            className="text-error-500 text-xxs font-primary font-extraLight"
            style={{ ...customStyles }}
          >
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors && name && errors?.[name] && (
        <span
          className="text-error-500 text-xxs font-primary font-normal w-full text-wrap inline-block leading-[1.3]"
          style={{ ...customStyles }}
        >
          {errors?.[name]?.['message']?.toString()}
        </span>
      )}
    </div>
  )
}

export default MultiLineInput
